import { useMemo, useState } from "react";
import {
  Crown, FileCheck, Search, ExternalLink, Wand2, Play, Moon, Sun,
  ChevronsDown, ArrowUpRight, Loader2, Ban, Sparkles, Lightbulb,
  ArrowUp, ArrowDown, CornerDownRight, Link2, XCircle,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

/* ── Product plumbing ──────────────────────────────────────────────
   Product is a real field; source (JSD / EQ) is a reference mark, never
   a filter. All current tickets are Jira → Allocate + Operator / JSD. */
export const PRODUCTS = ["Allocate + Operator", "Core", "Guardian"];
const productOf = (t) => t.product || "Allocate + Operator";
const sourceOf = (t) => t.source || (productOf(t) === "Allocate + Operator" ? "JSD" : "EQ");

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function ddMon(v) {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  return `${String(d.getDate()).padStart(2, "0")} ${MONTHS[d.getMonth()]}`;
}
// Due-date urgency dot: red past due · amber ≤48h · green clear
function dueTone(v) {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  const diffH = (d.getTime() - Date.now()) / 36e5;
  if (diffH < 0) return "#C23A00";
  if (diffH <= 48) return "#E0B800";
  return "#0E6B3F";
}

function suggestionLabel(action) {
  switch (action) {
    case "close": return "Ready to close";
    case "followup_internal": return "Check with team";
    case "change_status": return "Status issue";
    case "awaiting_system_update": return "Needs dev work";
    case "test_fix": return "Test fix";
    case "notify_fix_ready": return "Fix ready";
    case "verify_fix": return "Verify fix";
    case "followup_dev": return "Check dev";
    case "respond": return "Response needed";
    default: return "Follow up";
  }
}

// Tone for a linked-ticket status chip (answers "can I close this yet")
function statusChip(status) {
  const s = (status || "").toLowerCase();
  if (s.includes("ready for testing") || s.includes("resolved") || s.includes("done") || s.includes("closed"))
    return "bg-[#E3F3E9] text-[#0E6B3F] border border-[#0E6B3F]/25";
  if (s.includes("development") || s.includes("progress"))
    return "bg-[#FFF7D1] text-[#6B5600] border border-[#E8D48A]";
  if (s.includes("customer"))
    return "bg-[#FFF7D1] text-[#6B5600] border border-[#E8D48A]";
  return "bg-[#F1EEE6] text-[#59554D] border border-[#DAD5CA]";
}

function SourceMark({ src }) {
  return (
    <span
      className="mono text-[9px] font-semibold px-1 py-px rounded-sm bg-[#F1EEE6] text-[#8A857A] shrink-0"
      title={src === "JSD" ? "Jira Service Desk" : "EQUIPR native"}
    >
      {src}
    </span>
  );
}

const GRID = "grid grid-cols-[150px_150px_minmax(220px,1fr)_150px_92px_82px_82px_150px_184px] items-center gap-x-3";

const COLS = [
  { key: "key", label: "Ticket" },
  { key: "product", label: "Product" },
  { key: "summary", label: "Summary" },
  { key: "status", label: "Status" },
  { key: "due_date", label: "Due Date" },
  { key: "created", label: "Created" },
  { key: "updated", label: "Updated" },
  { key: "organization", label: "Client" },
];

export function AgentQueueTable({
  tickets = [],
  vipLookup = {},
  ticketSuggestions = {},
  isSnoozed,
  isAcknowledged,
  jiraUrl,
  productTab, setProductTab,
  queueTab, setQueueTab,
  search, setSearch,
  vipActive, setVipActive,
  statusFilter, setStatusFilter,
  priorityFilter, setPriorityFilter,
  aiFilter, setAiFilter,
  myEscalationsCount = 0,
  renderEscalations,
  // handlers
  triageKey, onTriage,
  onStart, onUnsnooze, onSnooze, onSendBottom, onEscalate,
  onIgnoreSuggestion, onCloseStale, onOpenRunbook, onAckSilent,
  // new-tab bulk selection
  selectedNewKeys, toggleSelectKey, selectAllVisibleNew, clearSelection,
  bulkMoveSelectedToActive, bulkAcking,
  fullscreen = false,
}) {
  const [sortCol, setSortCol] = useState("due_date");
  const [sortDir, setSortDir] = useState("asc"); // "what is about to breach" is the primary order

  const onSort = (col) => {
    if (sortCol === col) { setSortDir((d) => (d === "asc" ? "desc" : "asc")); }
    else { setSortCol(col); setSortDir("asc"); }
  };

  // Open tickets only (exclude terminal + escalated statuses)
  const openTickets = useMemo(() => tickets.filter((t) => {
    const s = (t.status || "").toLowerCase();
    return !s.includes("resolved") && !s.includes("closed") && !s.includes("done") &&
           !s.includes("cancelled") && !s.includes("escalat");
  }), [tickets]);

  // Product tab counts (assigned open tickets per product)
  const productCounts = useMemo(() => {
    const c = { All: openTickets.length, "Allocate + Operator": 0, Core: 0, Guardian: 0 };
    openTickets.forEach((t) => { c[productOf(t)] = (c[productOf(t)] || 0) + 1; });
    return c;
  }, [openTickets]);

  // Everything after the product tab: within the selected product only
  const inProduct = useMemo(() => (
    productTab === "all" ? openTickets : openTickets.filter((t) => productOf(t) === productTab)
  ), [openTickets, productTab]);

  const matchesFilters = (t) => {
    const status = (t.status || "").toLowerCase();
    if (vipActive && !vipLookup[(t.organization || "").toLowerCase()]?.is_vip) return false;
    if (aiFilter !== "all") {
      const has = !!ticketSuggestions[t.key];
      if (aiFilter === "with" && !has) return false;
      if (aiFilter === "without" && has) return false;
    }
    if (search) {
      const q = search.toLowerCase();
      if (!t.key?.toLowerCase().includes(q) && !t.summary?.toLowerCase().includes(q) && !t.organization?.toLowerCase().includes(q)) return false;
    }
    if (statusFilter !== "all" && !status.includes(statusFilter.toLowerCase())) return false;
    if (priorityFilter !== "all" && t.priority !== priorityFilter) return false;
    return true;
  };

  const filtered = useMemo(() => inProduct.filter(matchesFilters),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inProduct, vipActive, aiFilter, search, statusFilter, priorityFilter, ticketSuggestions, vipLookup]);

  const newCount = filtered.filter((t) => !isSnoozed(t.key) && !isAcknowledged(t.key)).length;
  const activeCount = filtered.filter((t) => !isSnoozed(t.key) && isAcknowledged(t.key)).length;
  const snoozedCount = filtered.filter((t) => isSnoozed(t.key)).length;

  const inTab = useMemo(() => filtered.filter((t) => {
    if (queueTab === "snoozed") return isSnoozed(t.key);
    if (isSnoozed(t.key)) return false;
    return queueTab === "new" ? !isAcknowledged(t.key) : isAcknowledged(t.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [filtered, queueTab]);

  const sorted = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    const val = (t) => {
      switch (sortCol) {
        case "key": return t.key || "";
        case "product": return productOf(t);
        case "summary": return (t.summary || "").toLowerCase();
        case "status": return (t.status || "").toLowerCase();
        case "organization": return (t.organization || "").toLowerCase();
        case "due_date":
        case "created":
        case "updated": {
          const d = t[sortCol] ? new Date(t[sortCol]).getTime() : NaN;
          return isNaN(d) ? null : d;
        }
        default: return "";
      }
    };
    return [...inTab].sort((a, b) => {
      const va = val(a), vb = val(b);
      // Blanks always sink to the bottom regardless of direction
      if (va === null && vb === null) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [inTab, sortCol, sortDir]);

  const productTabs = [
    { id: "all", label: "All" },
    { id: "Allocate + Operator", label: "Allocate + Operator" },
    { id: "Core", label: "Core" },
    { id: "Guardian", label: "Guardian" },
  ];
  const stateTabs = [
    { id: "new", label: "New", count: newCount },
    { id: "active", label: "Active", count: activeCount },
    { id: "snoozed", label: "Snoozed", count: snoozedCount, icon: Moon, disabled: snoozedCount === 0 },
    { id: "escalated", label: "Escalated", count: myEscalationsCount, icon: ArrowUpRight },
  ];

  const visibleNewKeys = filtered.filter((t) => !isSnoozed(t.key) && !isAcknowledged(t.key)).map((t) => t.key);
  const allNewSelected = visibleNewKeys.length > 0 && visibleNewKeys.every((k) => selectedNewKeys?.has(k));

  return (
    <div className="flex flex-col min-h-0 h-full" data-testid="agent-queue-table">
      {/* Product tabs (product is a tab, above the state tabs) */}
      <div className="flex items-center gap-1 flex-wrap border-b border-[#DAD5CA]" data-testid="product-tabs">
        {productTabs.map((p) => {
          const count = productCounts[p.id === "all" ? "All" : p.id] || 0;
          const active = productTab === p.id;
          const zero = count === 0 && p.id !== "all";
          return (
            <button
              key={p.id}
              onClick={() => setProductTab(p.id)}
              data-testid={`product-tab-${p.id.replace(/[^a-z]/gi, "-").toLowerCase()}`}
              className={`px-3 py-2 text-[13px] transition-colors flex items-center gap-2 border-b-2 -mb-px ${
                active ? "text-[#1A1A1A] font-bold border-[#1A1A1A]"
                : zero ? "text-[#8A857A] font-medium border-transparent"
                : "text-[#59554D] font-medium border-transparent hover:text-[#1A1A1A]"
              }`}
            >
              {p.label}
              <span className={`mono text-[11px] ${zero ? "text-[#8A857A]" : active ? "text-[#1A1A1A]" : "text-[#59554D]"}`}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Search + within-product filters */}
      <div className="flex items-center gap-2 py-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1A1A]" />
          <Input
            placeholder="Ticket key, summary or client"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-16 h-[42px] bg-[#FFFFFF] border-[1.5px] border-[#1A1A1A] rounded-[9px] text-sm focus-visible:ring-0 focus-visible:border-[#1A1A1A]"
            data-testid="queue-search-input"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 mono text-[10.5px] text-[#8A857A]">
            {search ? `${filtered.length} of ${inProduct.length}` : inProduct.length}
          </span>
        </div>
        <Button
          size="sm"
          variant={vipActive ? "default" : "outline"}
          onClick={() => setVipActive(!vipActive)}
          className={`h-9 px-3 text-xs ${vipActive ? "bg-[#C9A400] text-[#1A1A1A]" : "border-[#DAD5CA] bg-transparent text-[#59554D] hover:bg-[#F4F2ED] hover:text-[#1A1A1A]"}`}
          data-testid="queue-vip-filter"
        >
          <Crown className="w-3 h-3 mr-1" /> VIP
        </Button>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[130px] h-9 bg-[#F4F2ED] border-[#DAD5CA] text-xs" data-testid="queue-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-[#FFFFFF] border-[#DAD5CA]">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="support">Waiting For Support</SelectItem>
            <SelectItem value="customer">Waiting For Customer</SelectItem>
            <SelectItem value="development">Pending Development Work</SelectItem>
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-[120px] h-9 bg-[#F4F2ED] border-[#DAD5CA] text-xs" data-testid="queue-priority-filter">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent className="bg-[#FFFFFF] border-[#DAD5CA]">
            <SelectItem value="all">All Priority</SelectItem>
            <SelectItem value="Highest">Highest</SelectItem>
            <SelectItem value="High">High</SelectItem>
            <SelectItem value="Medium">Medium</SelectItem>
            <SelectItem value="Low">Low</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline" size="sm"
          onClick={() => setAiFilter(aiFilter === "all" ? "with" : aiFilter === "with" ? "without" : "all")}
          className={`h-9 text-xs px-3 ${aiFilter === "with" ? "bg-[#FFF7D1] border-[#E0B800] text-[#6B5600]" : aiFilter === "without" ? "bg-[#E3F3E9] border-[#0E6B3F] text-[#0E6B3F]" : "border-[#DAD5CA] text-[#59554D]"}`}
          data-testid="queue-ai-filter"
        >
          <Sparkles className="w-3 h-3 mr-1" />
          {aiFilter === "all" ? "AI" : aiFilter === "with" ? "Has AI" : "No AI"}
        </Button>
        {(search || vipActive || statusFilter !== "all" || priorityFilter !== "all" || aiFilter !== "all") && (
          <Button
            variant="outline" size="sm"
            onClick={() => { setSearch(""); setVipActive(false); setStatusFilter("all"); setPriorityFilter("all"); setAiFilter("all"); }}
            className="h-9 text-xs px-3 border-[#C23A00] text-[#C23A00] hover:bg-[#FFE9E0]"
            data-testid="queue-clear-filters"
          >
            <XCircle className="w-3 h-3 mr-1" /> Clear
          </Button>
        )}
      </div>

      {/* State tabs */}
      <div className="flex items-center gap-1 border-b border-[#DAD5CA] flex-wrap" data-testid="state-tabs">
        {stateTabs.map((tab) => {
          const active = queueTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => { if (!tab.disabled) { setQueueTab(tab.id); clearSelection && clearSelection(); } }}
              disabled={tab.disabled}
              data-testid={`state-tab-${tab.id}`}
              className={`px-3 py-2 text-[13px] transition-colors flex items-center gap-2 border-b-2 -mb-px ${
                active ? "text-[#1A1A1A] font-bold border-[#1A1A1A]"
                : tab.disabled ? "text-[#8A857A] cursor-not-allowed border-transparent font-medium"
                : "text-[#59554D] font-medium border-transparent hover:text-[#1A1A1A]"
              }`}
            >
              {tab.icon && <tab.icon className="w-3 h-3" />}
              {tab.label}
              <span className={`mono text-[11px] ${tab.count === 0 ? "text-[#8A857A]" : active ? "text-[#1A1A1A]" : "text-[#59554D]"}`}>{tab.count}</span>
            </button>
          );
        })}
        {/* New-tab bulk selection */}
        {queueTab === "new" && visibleNewKeys.length > 0 && (
          <div className="ml-auto flex items-center gap-2 py-1">
            <button
              onClick={() => (allNewSelected ? clearSelection() : selectAllVisibleNew(visibleNewKeys))}
              className="text-xs text-[#59554D] hover:text-[#1A1A1A]"
              data-testid="queue-select-all"
            >
              {allNewSelected ? "Clear selection" : `Select all (${visibleNewKeys.length})`}
            </button>
            {selectedNewKeys?.size > 0 && (
              <div className="flex items-center gap-2 bg-[#FFF8D6] border border-[#E0B800] rounded-md px-2 py-1">
                <span className="text-xs text-[#1A1A1A] font-medium">{selectedNewKeys.size} selected</span>
                <Button size="sm" onClick={bulkMoveSelectedToActive} disabled={bulkAcking}
                  className="bg-[#0E6B3F] hover:bg-[#0B4F2E] text-[#FFFFFF] h-7 px-2 text-xs" data-testid="queue-bulk-move-active">
                  {bulkAcking ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
                  Move to Active
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto min-h-0 mt-1">
        {queueTab === "escalated" ? (
          <div className="pt-3">{renderEscalations && renderEscalations()}</div>
        ) : sorted.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-[#59554D] text-sm">
              {inProduct.length === 0
                ? (productTab === "Core" || productTab === "Guardian"
                    ? `No ${productTab} tickets assigned to you yet.`
                    : "No open tickets — your queue is clear.")
                : "No tickets match these filters."}
            </p>
          </div>
        ) : (
          <div className="min-w-[1360px]">
            {/* Column header row (sortable) */}
            <div className={`${GRID} px-3 py-2 border-b border-[#DAD5CA] sticky top-0 bg-[#FBFAF7] z-10`} data-testid="queue-col-headers">
              {COLS.map((c) => {
                const activeSort = sortCol === c.key;
                const alignRight = ["due_date", "created", "updated"].includes(c.key);
                return (
                  <button
                    key={c.key}
                    onClick={() => onSort(c.key)}
                    data-testid={`sort-${c.key}`}
                    className={`flex items-center gap-1 text-[10px] uppercase tracking-[0.06em] font-semibold ${alignRight ? "justify-end" : ""} ${activeSort ? "text-[#1A1A1A]" : "text-[#8A857A] hover:text-[#59554D]"}`}
                  >
                    {c.label}
                    {activeSort && (sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
                  </button>
                );
              })}
              <span className="text-[10px] uppercase tracking-[0.06em] font-semibold text-[#8A857A] text-right pr-1">Actions</span>
            </div>

            {sorted.map((t) => (
              <QueueTableRow
                key={t.key}
                t={t}
                suggestion={ticketSuggestions[t.key]}
                vipLookup={vipLookup}
                jiraUrl={jiraUrl}
                queueTab={queueTab}
                acked={isAcknowledged(t.key)}
                triageKey={triageKey}
                onTriage={onTriage}
                onStart={onStart}
                onUnsnooze={onUnsnooze}
                onSnooze={onSnooze}
                onSendBottom={onSendBottom}
                onEscalate={onEscalate}
                onIgnoreSuggestion={onIgnoreSuggestion}
                onCloseStale={onCloseStale}
                onOpenRunbook={onOpenRunbook}
                onAckSilent={onAckSilent}
                selected={selectedNewKeys?.has(t.key)}
                onToggleSelect={toggleSelectKey}
              />
            ))}
            <p className="text-[11px] text-[#8A857A] px-3 py-2" data-testid="queue-sort-note">
              Sorted by {COLS.find((c) => c.key === sortCol)?.label || "Due Date"} {sortDir === "asc" ? "ascending" : "descending"} · click a column to re-sort
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function QueueTableRow({
  t, suggestion, vipLookup, jiraUrl, queueTab, acked,
  triageKey, onTriage, onStart, onUnsnooze, onSnooze, onSendBottom, onEscalate,
  onIgnoreSuggestion, onCloseStale, onOpenRunbook, onAckSilent,
  selected, onToggleSelect,
}) {
  const orgKey = (t.organization || "").toLowerCase();
  const isVip = vipLookup[orgKey]?.is_vip;
  const slaClient = vipLookup[orgKey]?.has_sla;
  const src = sourceOf(t);
  const isJira = src === "JSD";
  const spine = t.sla_breached ? "#C23A00" : (t.sla_risk || suggestion) ? "#E0B800" : "transparent";
  const chip = t.sla_breached
    ? { cls: "bg-[#FFE9E0] text-[#9A3200] border border-[#F3BFA8]", label: "SLA breached" }
    : t.priority === "Highest" ? { cls: "bg-[#FFE1E1] text-[#8E1212] border border-[#EBAFAF]", label: "Highest" }
    : t.priority === "High" ? { cls: "bg-[#FFF7D1] text-[#6B5600] border border-[#E8D48A]", label: "High" }
    : null;
  const dueDot = dueTone(t.due_date);
  const links = Array.isArray(t.linked_issues) ? t.linked_issues : [];

  const IconBtn = ({ onClick, title, testid, children, className = "" }) => (
    <button type="button" onClick={onClick} title={title} data-testid={testid}
      className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${className}`}>
      {children}
    </button>
  );

  return (
    <div
      className="group border-b border-[#EFECE4] hover:bg-[#FBFAF7] transition-colors"
      style={{ boxShadow: spine !== "transparent" ? `inset 3px 0 0 ${spine}` : "none" }}
      data-testid={`queue-row-${t.key}`}
    >
      <div className={`${GRID} px-3 py-2.5`}>
        {/* Ticket key + source mark (+ new-tab checkbox) */}
        <div className="flex items-center gap-1.5 min-w-0">
          {queueTab === "new" && (
            <input type="checkbox" checked={!!selected} onChange={() => onToggleSelect && onToggleSelect(t.key)}
              className="w-3.5 h-3.5 accent-[#8A6D00] cursor-pointer shrink-0" aria-label={`Select ${t.key}`}
              data-testid={`select-${t.key}`} />
          )}
          {isJira ? (
            <a href={`${jiraUrl}/${t.key}`} target="_blank" rel="noopener noreferrer"
              className="mono text-[11px] font-semibold text-[#8A6D00] hover:underline truncate">{t.key}</a>
          ) : (
            <span className="mono text-[11px] font-semibold text-[#1A1A1A] truncate">{t.key}</span>
          )}
          <SourceMark src={src} />
        </div>

        {/* Product */}
        <span className="text-[11.5px] text-[#59554D] truncate">{productOf(t)}</span>

        {/* Summary + VIP / SLA glyphs + inline chip */}
        <div className="flex items-center gap-1.5 min-w-0">
          {chip && <span className={`text-[9.5px] font-medium px-1.5 py-0.5 rounded shrink-0 ${chip.cls}`}>{chip.label}</span>}
          {isVip && <Crown className="w-3.5 h-3.5 text-[#C9A400] shrink-0" />}
          {slaClient && <FileCheck className="w-3.5 h-3.5 text-[#1D4ED8] shrink-0" />}
          <span className="text-[12.5px] text-[#1A1A1A] font-medium truncate">{t.summary}</span>
        </div>

        {/* Status (display vocabulary) */}
        <span className="text-[11.5px] text-[#59554D] truncate">{t.status}</span>

        {/* Due Date + urgency dot */}
        <span className="flex items-center justify-end gap-1.5 mono text-[11px]">
          {dueDot && <span className="w-[6px] h-[6px] rounded-full shrink-0" style={{ background: dueDot }} />}
          {ddMon(t.due_date) ? <span className="text-[#1A1A1A]">{ddMon(t.due_date)}</span> : <span className="text-[#B5AFA3]">—</span>}
        </span>
        {/* Created */}
        <span className="mono text-[11px] text-[#59554D] text-right">{ddMon(t.created) || <span className="text-[#B5AFA3]">—</span>}</span>
        {/* Updated */}
        <span className="mono text-[11px] text-[#59554D] text-right">{ddMon(t.updated) || <span className="text-[#B5AFA3]">—</span>}</span>

        {/* Client */}
        {t.organization ? (
          <button type="button" onClick={() => onOpenRunbook && onOpenRunbook(t.organization)}
            className="text-[11.5px] text-[#8A6D00] hover:underline truncate text-left" title="Open client runbook"
            data-testid={`runbook-link-${t.key}`}>
            {t.organization}
          </button>
        ) : <span className="text-[#B5AFA3]">—</span>}

        {/* Actions — source-dependent affordances are hidden, never disabled */}
        <div className="flex items-center gap-0.5 justify-end">
          <IconBtn onClick={() => onTriage && onTriage(t.key)} title="Triage with AI" testid={`triage-${t.key}`}
            className="text-[#59554D] hover:bg-[#F4F2ED] hover:text-[#1A1A1A]">
            {triageKey === t.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
          </IconBtn>
          {queueTab === "snoozed" ? (
            <IconBtn onClick={() => onUnsnooze && onUnsnooze(t.key)} title="Wake up now" testid={`unsnooze-${t.key}`}
              className="text-[#1D4ED8] hover:bg-[#EFF6FF]"><Sun className="w-4 h-4" /></IconBtn>
          ) : !acked ? (
            <IconBtn onClick={() => onStart && onStart(t.key)} title="Start working" testid={`start-working-${t.key}`}
              className="text-[#0E6B3F] hover:bg-[#E3F3E9]"><Play className="w-4 h-4" /></IconBtn>
          ) : (
            <>
              <IconBtn onClick={() => onSendBottom && onSendBottom(t)} title="Send to bottom of queue" testid={`send-to-bottom-${t.key}`}
                className="text-[#59554D] hover:bg-[#F4F2ED] hover:text-[#1A1A1A]"><ChevronsDown className="w-4 h-4" /></IconBtn>
              <IconBtn onClick={() => onSnooze && onSnooze(t)} title="Snooze" testid={`snooze-${t.key}`}
                className="text-[#1D4ED8] hover:bg-[#EFF6FF]"><Moon className="w-4 h-4" /></IconBtn>
            </>
          )}
          <IconBtn onClick={() => onEscalate && onEscalate(t)} title="Escalate" testid={`escalate-${t.key}`}
            className="text-[#8A6D00] hover:bg-[#F4F2ED]"><ArrowUpRight className="w-4 h-4" /></IconBtn>
          {isJira && (
            <a href={`${jiraUrl}/${t.key}`} target="_blank" rel="noopener noreferrer"
              onClick={() => onAckSilent && onAckSilent(t.key)}
              className="w-8 h-8 rounded-md flex items-center justify-center shrink-0 bg-[#FFFFFF] border border-[#C9C3B6] text-[#59554D] hover:bg-[#F4F2ED] hover:text-[#1A1A1A]"
              title="Open in Jira" data-testid={`open-jira-${t.key}`}>
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>
      </div>

      {/* Linked tickets — indented sub-rows, status chip is the point */}
      {links.map((l, i) => (
        <div key={`${l.key}-${i}`} className="flex items-center gap-2 pl-8 pr-3 py-1.5 border-t border-dashed border-[#EFECE4]" data-testid={`linked-${t.key}-${l.key}`}>
          <CornerDownRight className="w-3 h-3 text-[#8A857A] shrink-0" />
          <span className="text-[9px] uppercase tracking-[0.08em] font-semibold text-[#8A857A] shrink-0">Linked</span>
          {isJira ? (
            <a href={`${jiraUrl}/${l.key}`} target="_blank" rel="noopener noreferrer"
              className="mono text-[10.5px] font-semibold text-[#8A6D00] hover:underline shrink-0">{l.key}</a>
          ) : (
            <span className="mono text-[10.5px] font-semibold text-[#1A1A1A] shrink-0">{l.key}</span>
          )}
          <SourceMark src={sourceOf(l)} />
          <span className="text-[11.5px] text-[#59554D] truncate flex-1 min-w-0">{l.summary}</span>
          {l.status && <span className={`text-[9.5px] font-medium px-1.5 py-0.5 rounded shrink-0 ${statusChip(l.status)}`}>{l.status}</span>}
        </div>
      ))}

      {/* AI note — indented second line with labelled kind + dismiss */}
      {suggestion && (
        <div className="flex items-center gap-2 pl-8 pr-3 py-1.5 border-t border-[#F0EDE6] bg-[#FBFAF7] text-xs" data-testid={`ai-note-${t.key}`}>
          {suggestion.ai_analyzed ? <Sparkles className="w-3.5 h-3.5 text-[#8A6D00] shrink-0" /> : <Lightbulb className="w-3.5 h-3.5 text-[#59554D] shrink-0" />}
          <span className="font-medium text-[#6B5600] shrink-0">
            {suggestion.action === "followup_internal" && suggestion.internal_followup_with
              ? `Check with ${suggestion.internal_followup_with.split(" ")[0]}`
              : suggestionLabel(suggestion.action)}:
          </span>
          <span className="text-[#59554D] truncate flex-1 min-w-0">{suggestion.reason}</span>
          {suggestion.action === "close" && onCloseStale && (
            <button type="button" onClick={() => onCloseStale(suggestion, "close")}
              className="text-[#0E6B3F] hover:underline shrink-0 font-medium" data-testid={`close-ticket-${suggestion.ticket_key}`}>Close</button>
          )}
          {onIgnoreSuggestion && (
            <button type="button" onClick={() => onIgnoreSuggestion(t, suggestion)}
              className="text-[#8A857A] hover:text-[#C23A00] shrink-0" title="Dismiss" data-testid={`ignore-suggestion-${suggestion.ticket_key}`}>
              <Ban className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
